import moment, { Moment } from "moment";
import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { HolidayExceptionDate, ScheduleItem } from "@lib/types";

/** Schedule calendar type. */
export enum ScheduleCalendarType {
    today = "today",
    day = "day",
    week = "week",
    workweek = "workweek",
    month = "month",
}

/** Schedule calendar store. */
export class ScheduleCalendarStore {

    /** ID. */
    public id: string;

    /** Mode. */
    public mode: ScheduleCalendarType = ScheduleCalendarType.day;

    /** Date. */
    public date: Moment = moment();

    /** Schedules. */
    public schedules: readonly ScheduleItem[] = [];

    /** Holiday exceptions. */
    public holidayExceptions: readonly HolidayExceptionDate[] = [];

    public constructor(id: string) {
        this.id = id;

        makeAutoObservable(this);
    }

    /**
     * Handles change.
     * @param name Name of field.
     * @param value New value.
     */
    public onChange = <T extends keyof this>(
        name: T,
        value: this[T],
    ): void => {
        this[name] = value as never;
    };

    /**
     * Adds new schedule item.
     * @param item New schedule item.
     */
    public addSchedule(item: ScheduleItem): void {
        this.schedules = [...this.schedules, item];
    }

    /**
     * Replaces ID of schedule item.
     * @param oldId Old ID.
     * @param newId New ID.
     */
    public replaceIdOfScheduleItem(oldId: ScheduleItem["id"], newId: ScheduleItem["id"]): void {
        const scheduleItem = this.schedules.find(schedule => schedule.id === oldId);
        if (scheduleItem == null) {
            return;
        }
        const newItem: ScheduleItem = {
            ...scheduleItem,
            id: newId,
        };
        const newItems = this.schedules.filter(item => item.id !== oldId);
        newItems.push(newItem);
        this.schedules = newItems;
    }

    /**
     * Whether has schedule item with ID.
     * @param id Schedule item ID.
     */
    public hasScheduleItemWithId(id: ScheduleItem["id"]): boolean {
        return this.schedules.some(item => item.id === id);
    }

    /**
     * Updates schedule item data.
     * @param item Schedule item.
     */
    public updateSchedule(item: ScheduleItem): void {
        const { id } = item;
        const newItems = this.schedules.filter(scheduleItem => scheduleItem.id !== id);
        newItems.push(item);
        this.schedules = newItems;
    }

    /**
     * Gets schedule item by ID.
     * @param id Schedule item ID.
     */
    public getScheduleById(id: ScheduleItem["id"]): ScheduleItem | null {
        return this.schedules.find(({ id: scheduleId }) => scheduleId === id) ?? null;
    }

    /**
     * Gets child schedule items.
     * @param id Parent schedule item ID.
     */
    public getChildSchedules(id: ScheduleItem["id"]): ScheduleItem[] {
        return this.schedules.filter(({ parentId }) => parentId === id);
    }

    /**
     * Deletes schedule item by ID.
     * @param id Schedule item ID.
     */
    public deleteScheduleById(id: ScheduleItem["id"]): void {
        this.schedules = this.schedules.filter(item => item.id !== id);
    }
}

/** Schedule calendars store. */
export class ScheduleCalendarsStore {

    /** Stores. */
    public values: { [key: string]: ScheduleCalendarStore } = {};

    public constructor() {
        makeAutoObservable(this);
    }

    /** Adds new store. */
    public add = (): { id: string; state: ScheduleCalendarStore } => {
        const id = uuidv4();
        const state = new ScheduleCalendarStore(id);
        this.values[id] = state;
        return { id, state };
    };

    /**
     * Finds schedule store by ID.
     * @param id ID.
     */
    public find = (id: string): ScheduleCalendarStore => this.values[id];
}
